import React, { useEffect, useMemo, useState } from "react";
import { APIProvider, Map as GMap, useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import api from "@/lib/api";
import { formatValue, formatNumber, PALETTES } from "@/lib/chartTheme";
import { activeRanges, rangeColorOf, advColorMode, advStyleResolver } from "@/lib/chartData";
import { KeyRound, AlertTriangle } from "lucide-react";

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const coord = (x) => (x == null || x === "" ? NaN : Number(x));

// Compact Google map themes (item 88.19)
const GM_THEMES = {
  dark: [
    { elementType: "geometry", stylers: [{ color: "#212121" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#383838" }] },
  ],
  night: [
    { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
  ],
  silver: [
    { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  ],
  retro: [
    { elementType: "geometry", stylers: [{ color: "#ebe3cd" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#523735" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#b9d3c2" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#f5f1e6" }] },
  ],
};

let _keyCache = {};
async function fetchKey(publicToken) {
  const ck = publicToken || "auth";
  if (_keyCache[ck] !== undefined) return _keyCache[ck];
  try {
    const { data } = publicToken
      ? await api.get(`/dashboards/public/google-maps-key/${publicToken}`)
      : await api.get("/tenants/current/google-maps");
    _keyCache[ck] = data?.key || "";
  } catch {
    _keyCache[ck] = "";
  }
  return _keyCache[ck];
}
export function invalidateGmapsKeyCache() { _keyCache = {}; }

// Imperative layers: markers/bubbles/cluster/heat/route + traffic/transit (item 88.3-9,18)
function MapLayers({ pts, geo, mode }) {
  const map = useMap();
  const ptsKey = JSON.stringify(pts.map((p) => [p.lat, p.lng, p.color, p.scale, p.cat]));
  const geoKey = JSON.stringify(geo);
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const g = window.google.maps;
    const objs = [];
    let clusterer = null;
    const iw = new g.InfoWindow();
    if (mode === "heat" && g.visualization) {
      const heat = new g.visualization.HeatmapLayer({
        data: pts.map((p) => ({ location: new g.LatLng(p.lat, p.lng), weight: Math.abs(p.v) || 1 })),
        radius: Number(geo.heatRadius) || 24,
        opacity: Number(geo.opacity ?? 0.7),
      });
      heat.setMap(map);
      objs.push(heat);
    } else {
      const markers = pts.map((p) => {
        const mk = new g.Marker({
          position: { lat: p.lat, lng: p.lng },
          title: p.label || "",
          icon: {
            path: g.SymbolPath.CIRCLE,
            scale: p.scale,
            fillColor: p.color,
            fillOpacity: Number(geo.opacity ?? 0.85),
            strokeColor: geo.borderColor || "#ffffff",
            strokeWeight: Number(geo.borderWidth ?? 1),
          },
        });
        mk.addListener("mouseover", () => { iw.setContent(p.html); iw.open({ map, anchor: mk }); });
        mk.addListener("click", () => {
          iw.setContent(p.html); iw.open({ map, anchor: mk });
          if (p.onClick) p.onClick();
        });
        return mk;
      });
      if (geo.cluster) clusterer = new MarkerClusterer({ map, markers });
      else markers.forEach((m) => m.setMap(map));
      objs.push(...markers);
      if (mode === "route" && pts.length > 1) {
        const line = new g.Polyline({
          path: pts.map((p) => ({ lat: p.lat, lng: p.lng })),
          strokeColor: geo.routeColor || "#2B4EFF",
          strokeWeight: Number(geo.routeWidth) || 3,
          strokeOpacity: 0.9,
          icons: [{ icon: { path: g.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.2, strokeColor: geo.routeColor || "#2B4EFF" }, offset: "40px", repeat: "120px" }],
        });
        line.setMap(map);
        objs.push(line);
      }
    }
    if (geo.traffic) { const t = new g.TrafficLayer(); t.setMap(map); objs.push(t); }
    if (geo.transit) { const t = new g.TransitLayer(); t.setMap(map); objs.push(t); }
    if (pts.length) {
      const b = new g.LatLngBounds();
      pts.forEach((p) => b.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(b, 48);
      if (pts.length === 1) map.setZoom(Math.min(map.getZoom() || 10, 12));
    }
    return () => {
      iw.close();
      if (clusterer) { try { clusterer.clearMarkers(); } catch { /* noop */ } }
      objs.forEach((o) => { try { o.setMap && o.setMap(null); } catch { /* noop */ } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ptsKey, geoKey, mode]);
  return null;
}

function CenterCard({ icon: Icon, title, sub, danger }) {
  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="text-center px-4 max-w-[320px]">
        <Icon className={"w-7 h-7 mx-auto mb-2 " + (danger ? "text-[color:var(--danger)]" : "text-[color:var(--ink-3)]")} />
        <div className="text-sm font-medium text-[color:var(--ink-2)]">{title}</div>
        <div className="text-xs text-[color:var(--ink-3)] mt-1">{sub}</div>
      </div>
    </div>
  );
}

export default function GoogleMapWidget({ data, config, onPointFilter, publicToken }) {
  const [key, setKey] = useState(null);
  const [authFail, setAuthFail] = useState(false);
  const geo = config.geo || {};
  const opts = config.options || {};
  const mode = geo.gMode || "markers"; // markers | bubbles | cluster legacy via geo.cluster | heat | route
  useEffect(() => {
    let a = true;
    fetchKey(publicToken).then((k) => a && setKey(k));
    return () => { a = false; };
  }, [publicToken]);
  useEffect(() => {
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => setAuthFail(true);
    return () => { window.gm_authFailure = prev; };
  }, []);

  const rows = data?.rows || [];
  const m0 = (config.measures || [])[0];
  const measureKey = m0 ? (m0.key || m0.alias || `${m0.agg || "sum"}_${m0.field}`) : null;

  const { pts, excluded, catColors } = useMemo(() => {
    if (!geo.lat || !geo.lon) return { pts: [], excluded: 0, catColors: null };
    const palette = PALETTES[opts.palette] || PALETTES["Eazy Core"];
    let cats = null;
    if (geo.colorField) {
      cats = {};
      let ci = 0;
      rows.forEach((r) => {
        const c = r[geo.colorField];
        if (c != null && cats[String(c)] === undefined) { cats[String(c)] = (geo.catColors || {})[String(c)] || palette[ci % palette.length]; ci += 1; }
      });
    }
    const valid = [];
    let bad = 0;
    const vals = rows.map((r) => (measureKey ? Number(r[measureKey]) : NaN)).filter(Number.isFinite);
    const vMin = vals.length ? Math.min(...vals) : 0;
    const vMax = vals.length ? Math.max(...vals) : 1;
    const ranges = activeRanges(opts);
    let mm = {};
    if (advColorMode(opts) === "lmh" && vals.length) mm = { min: vMin, max: vMax };
    const advResolve = advStyleResolver(opts, mm);
    const minS = Number(geo.minSize ?? 6), maxS = Number(geo.maxSize ?? 18);
    const fixedS = geo.fixedSize != null && geo.fixedSize !== "" ? Number(geo.fixedSize) : null;
    const maxAbs = Math.max(Math.abs(vMin), Math.abs(vMax), 1e-9);
    const extras = config.tooltip_measures || [];
    const ttLabels = (opts.tooltip || {}).labels || {};
    rows.forEach((r) => {
      const lat = coord(r[geo.lat]), lng = coord(r[geo.lon]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) { bad += 1; return; }
      const v = measureKey ? Number(r[measureKey]) : NaN;
      const cat = cats && r[geo.colorField] != null ? String(r[geo.colorField]) : null;
      const advSt = advResolve ? advResolve(v, cat) : null;
      const advC = advSt && typeof advSt.color === "string" ? advSt.color : null;
      const rangeC = ranges.length && Number.isFinite(v) ? rangeColorOf(v, ranges) : null;
      const color = advC || rangeC || (cat && cats[cat]) || geo.color || "#2B4EFF";
      const scale = fixedS != null ? fixedS : (Number.isFinite(v) && measureKey ? minS + (maxS - minS) * Math.sqrt(Math.abs(v) / maxAbs) : minS);
      const label = geo.labelField ? r[geo.labelField] : (r.name ?? r[Object.keys(r)[0]]);
      const lines = [];
      if (cat) lines.push(`<div>${esc(geo.colorField)}: <b>${esc(cat)}</b></div>`);
      if (measureKey && Number.isFinite(v)) lines.push(`<div>${esc(measureKey.replace(/_/g, " "))}: <b>${esc(geo.valueFormat ? formatValue(v, geo.valueFormat) : formatNumber(v))}</b></div>`);
      extras.forEach((t) => {
        const k = `tt_${t.agg}_${t.field}`;
        const tv = r[k];
        if (tv == null) return;
        const lbl = ttLabels[k] || `${t.agg} ${t.field}`;
        const n = Number(tv);
        lines.push(`<div>${esc(lbl)}: <b>${esc(Number.isFinite(n) ? (t.format ? formatValue(n, t.format) : formatNumber(n)) : String(tv))}</b></div>`);
      });
      if (geo.showCoords !== false) lines.push(`<div style="color:#888">${lat.toFixed(4)}, ${lng.toFixed(4)}</div>`);
      const html = `<div style="font:12px system-ui;min-width:130px"><div style="font-weight:600;margin-bottom:2px">${esc(label ?? "")}</div>${lines.join("")}</div>`;
      valid.push({
        lat, lng, v, cat, color, scale, label: String(label ?? ""), html,
        onClick: onPointFilter && geo.colorField && cat != null ? () => onPointFilter(geo.colorField, cat) : null,
      });
    });
    return { pts: valid, excluded: bad, catColors: cats };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, JSON.stringify(geo), measureKey, JSON.stringify(opts.rangeColors || []), opts.advColorMode || "", JSON.stringify(opts.colorRules || []), JSON.stringify(opts.lmh || {}), JSON.stringify(config.tooltip_measures || [])]);

  if (!geo.lat || !geo.lon) return <CenterCard icon={AlertTriangle} title="Map fields missing" sub="Assign Latitude and Longitude fields in the Data panel." />;
  if (key === null) return <CenterCard icon={KeyRound} title="Loading map…" sub="Fetching Google Maps configuration." />;
  if (!key) {
    return (
      <div data-testid="gmap-no-key" className="h-full w-full">
        <CenterCard icon={KeyRound} title="Google Maps key not configured"
          sub="An admin can add the API key under Admin → Tenants → Google Maps. Until then, use the free Point Map widget." />
      </div>
    );
  }
  if (authFail) {
    return (
      <div data-testid="gmap-auth-fail" className="h-full w-full">
        <CenterCard danger icon={AlertTriangle} title="Google Maps key rejected"
          sub="The configured API key is invalid, expired or missing the Maps JavaScript API. Check the key in Admin settings." />
      </div>
    );
  }

  const legendEntries = catColors && geo.showLegend !== false ? Object.entries(catColors).slice(0, 8) : [];
  return (
    <div data-testid="google-map" className="h-full w-full relative rounded-xl overflow-hidden">
      <APIProvider apiKey={key} libraries={["visualization"]}>
        <GMap
          style={{ width: "100%", height: "100%" }}
          defaultCenter={{ lat: Number(geo.centerLat) || 21, lng: Number(geo.centerLng) || 15 }}
          defaultZoom={Number(geo.zoom) || 2}
          mapTypeId={geo.gMapType || "roadmap"}
          styles={GM_THEMES[geo.gTheme] || undefined}
          gestureHandling="greedy"
          streetViewControl={false}
          fullscreenControl
          reuseMaps
        >
          <MapLayers pts={pts} geo={geo} mode={mode} />
        </GMap>
      </APIProvider>
      {excluded > 0 && (
        <div data-testid="gmap-invalid-warning" className="absolute top-2 left-2 z-[5] rounded-lg bg-amber-50/95 border border-amber-200 text-amber-800 text-[10px] px-2 py-1 shadow">
          {excluded} record{excluded > 1 ? "s" : ""} excluded — invalid latitude/longitude
        </div>
      )}
      {legendEntries.length > 0 && (
        <div data-testid="gmap-legend" className="absolute bottom-6 left-2 z-[5] rounded-lg bg-white/90 backdrop-blur px-2 py-1.5 shadow border border-black/5 max-w-[45%]">
          {legendEntries.map(([cat, color]) => (
            <div key={cat} className="flex items-center gap-1.5 text-[10px] text-neutral-700 leading-4">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
              <span className="truncate">{cat}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
